import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { moneyLabel } from "./payloadDisplay";
import { fetchOrder } from "./checkoutApi";
import "./OrderPage.css";

/**
 * A-009 — Order confirmation renders only orderPayload fields.
 */
function OrderPage(props) {
  const number =
    (props.match && props.match.params && props.match.params.number) || "";
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchOrder(decodeURIComponent(number));
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Unable to load order.");
          setPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [number]);

  if (loading) {
    return (
      <div className="commerce-order commerce-order--loading">
        <p>Loading order…</p>
      </div>
    );
  }

  if (!payload || !payload.order) {
    return (
      <div className="commerce-order commerce-order--empty">
        <h1>Order</h1>
        {error ? <p className="commerce-order__error">{error}</p> : null}
        <Link className="btn btn-danger" to="/shop">
          Back to Shop
        </Link>
      </div>
    );
  }

  const order = payload.order;
  const items = order.items || [];
  const summary = order.summary || {};
  const messages = payload.messages || [];
  const customer = payload.customer || {};

  return (
    <div className="commerce-order">
      <Helmet>
        <title>{`Order ${order.number} | Pentecost+`}</title>
      </Helmet>

      <div className="commerce-order__inner">
        <p className="commerce-order__crumb">
          <Link to="/shop">Shop</Link>
          <span aria-hidden="true"> / </span>
          <span>Order</span>
        </p>

        <h1 className="commerce-order__title">Order {order.number}</h1>

        <p className="commerce-order__meta">
          Status: {order.status} · Payment: {order.payment_status}
        </p>

        {messages.map((m, i) => (
          <p key={i} className="commerce-order__message">
            {typeof m === "string" ? m : m.message}
          </p>
        ))}

        {customer.email || customer.name ? (
          <p className="commerce-order__customer">
            {customer.name ? <span>{customer.name}</span> : null}
            {customer.email ? <span> · {customer.email}</span> : null}
          </p>
        ) : null}

        <ul className="commerce-order__lines">
          {items.map((item) => (
            <li key={item.id}>
              <div>
                <strong>{(item.product && item.product.name) || "Item"}</strong>
                <span>
                  {" "}
                  × {item.quantity} · {item.type}
                </span>
              </div>
              <div>{moneyLabel(item.pricing, "line_subtotal")}</div>
            </li>
          ))}
        </ul>

        <dl className="commerce-order__summary">
          <div>
            <dt>Subtotal</dt>
            <dd>{moneyLabel(summary, "subtotal")}</dd>
          </div>
          <div>
            <dt>Discounts</dt>
            <dd>{moneyLabel(summary, "discounts")}</dd>
          </div>
          <div>
            <dt>Taxes</dt>
            <dd>
              {summary.taxes == null ? "—" : moneyLabel(summary, "taxes")}
            </dd>
          </div>
          <div>
            <dt>Shipping</dt>
            <dd>
              {summary.shipping == null
                ? "—"
                : moneyLabel(summary, "shipping")}
            </dd>
          </div>
          <div className="commerce-order__total">
            <dt>Total</dt>
            <dd>{moneyLabel(summary, "total")}</dd>
          </div>
        </dl>

        <p className="commerce-order__hint">
          Payment adapters will charge this order in a later milestone. No
          receipt email is sent yet.
        </p>

        <Link className="btn btn-danger" to="/shop">
          Continue shopping
        </Link>
      </div>
    </div>
  );
}

export default OrderPage;
